import { Route, Switch, Redirect, Link } from "wouter";
import { useTranslation } from "react-i18next";
import { Shield, LogOut, Settings } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { LandingPage } from "@/pages/Landing";
import { LoginPage } from "@/pages/Login";
import { DashboardPage } from "@/pages/Dashboard";
import { OpsCenterPage } from "@/pages/OpsCenter";
import { IncidentDetailPage } from "@/pages/IncidentDetail";
import { AssetsAdminPage } from "@/pages/admin/AssetsAdmin";
import { ModelsAdminPage } from "@/pages/admin/ModelsAdmin";
import { RecipientsAdminPage } from "@/pages/admin/RecipientsAdmin";
import { DemoLauncherPage } from "@/pages/admin/DemoLauncher";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { HeaderSwitcher } from "@/components/HeaderSwitcher";

/**
 * Routing model
 * -------------
 *   /              → public Landing page (or auto-redirect to /app if signed in)
 *   /login         → LoginPage (sends authenticated users to /app)
 *   /app           → operator Dashboard (auth required)
 *   /incidents/:id → operator IncidentDetail (auth required)
 *
 * Lesson 39: when changing post-login redirect URL, update EVERY
 * test helper that asserts toHaveURL(...). Currently just our own
 * Playwright suite uses these.
 *
 * Lesson 41: keep first-paint routes EAGER imported, not React.lazy.
 * Landing + Login are the most critical routes for unauthenticated
 * visitors and must paint without a Suspense flash.
 */

function ProtectedShell({ children }: { children: React.ReactNode }) {
  const { t } = useTranslation();
  const { user, loading, logout } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 text-slate-400 flex items-center justify-center text-sm">
        Loading…
      </div>
    );
  }
  if (!user) {
    return <Redirect to="/login" />;
  }

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100" data-testid="app-shell">
      <header className="border-b border-slate-800 px-6 py-3 flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <Shield className="text-teal-400 size-5" />
          <span className="text-teal-400 font-semibold">{t("appName")}</span>
          <span className="text-slate-500 text-xs hidden sm:inline">
            pilot · L1
          </span>
          <HeaderSwitcher />
        </div>
        <div className="flex items-center gap-4">
          <span
            className="text-xs text-slate-400 hidden sm:inline"
            data-testid="header-user"
          >
            {user.email} <span className="text-slate-600">·</span> {user.role}
          </span>
          {(user.role === "ADMIN" || user.role === "SUPERVISOR") && (
            <Link
              href="/app/admin/sites"
              data-testid="header-admin-link"
              className="text-xs text-slate-400 hover:text-slate-200 inline-flex items-center gap-1"
            >
              <Settings className="size-3" /> Admin
            </Link>
          )}
          <LanguageSwitcher />
          <button
            type="button"
            onClick={() => void logout()}
            data-testid="logout-button"
            className="text-xs text-slate-400 hover:text-slate-200 inline-flex items-center gap-1"
          >
            <LogOut className="size-3" /> {t("nav.logout")}
          </button>
        </div>
      </header>
      <main className="px-6 py-6 max-w-6xl mx-auto">{children}</main>
    </div>
  );
}

/**
 * Root route — show Landing to unauthenticated visitors, redirect signed-in
 * users straight to /app so they don't have to click through the marketing
 * page on every load.
 */
function RootRoute() {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen bg-[#0B1121] text-slate-400 flex items-center justify-center text-sm">
        Loading…
      </div>
    );
  }
  if (user) return <Redirect to="/app" />;
  return <LandingPage />;
}

export function App() {
  return (
    <Switch>
      <Route path="/" component={RootRoute} />
      <Route path="/login" component={LoginPage} />
      <Route path="/app">
        <ProtectedShell>
          <DashboardPage />
        </ProtectedShell>
      </Route>
      <Route path="/app/ops-center">
        <ProtectedShell>
          <OpsCenterPage />
        </ProtectedShell>
      </Route>
      <Route path="/app/admin/sites">
        <ProtectedShell>
          <AssetsAdminPage />
        </ProtectedShell>
      </Route>
      <Route path="/app/admin/models">
        <ProtectedShell>
          <ModelsAdminPage />
        </ProtectedShell>
      </Route>
      <Route path="/app/admin/recipients">
        <ProtectedShell>
          <RecipientsAdminPage />
        </ProtectedShell>
      </Route>
      <Route path="/app/admin/demo">
        <ProtectedShell>
          <DemoLauncherPage />
        </ProtectedShell>
      </Route>
      <Route path="/incidents/:id">
        <ProtectedShell>
          <IncidentDetailPage />
        </ProtectedShell>
      </Route>
      <Route>
        {/* 404 fallback — wouter doesn't show a styled 404 by default */}
        <NotFoundRoute />
      </Route>
    </Switch>
  );
}

function NotFoundRoute() {
  return (
    <div
      className="min-h-screen bg-[#0B1121] text-slate-100 flex flex-col items-center justify-center px-6 text-center"
      data-testid="not-found-page"
    >
      <Shield className="text-teal-400 size-10 mb-3" />
      <h1 className="text-3xl font-bold mb-2">404 — Page not found</h1>
      <p className="text-slate-400 max-w-sm mb-6">
        The page you tried to reach doesn&rsquo;t exist on this Sentinel deployment.
      </p>
      <a
        href="/"
        className="text-sm bg-teal-400 hover:bg-teal-300 text-slate-900 font-semibold px-4 py-2 rounded-md"
      >
        Back to home
      </a>
    </div>
  );
}
